import { PRODUCT, WEB_ROUTES } from "./config";
import {
  HOW_BUILT,
  howBuiltSection,
  type HowBuiltModelItem,
  type HowBuiltSectionMeta,
  type HowBuiltSource,
} from "./how-i-built-this-content";
import {
  MOVING_PARTS,
  type MovingPartProcess,
  type MovingPartScreen,
} from "./how-i-built-this-moving-parts";
import "./how-i-built-this.css";

let activePage: AbortController | undefined;

function text(value: string | number): string {
  const span = document.createElement("span");
  span.textContent = String(value);
  return span.innerHTML;
}

function sourceLink(source: HowBuiltSource): string {
  const external = source.href.startsWith("https://");
  return `<a href="${text(source.href)}"${external ? ' target="_blank" rel="noreferrer"' : ""}>${text(source.label)}<span aria-hidden="true"> ↗</span></a>`;
}

function sources(items: readonly HowBuiltSource[]): string {
  return `
    <details class="hib-sources">
      <summary>Sources and evidence</summary>
      <div class="hib-source-links">
        ${items.map(sourceLink).join("")}
      </div>
      <p>Repository links point to the private project on GitHub and may require access.</p>
    </details>
  `;
}

function chapter(meta: HowBuiltSectionMeta, body: string): string {
  return `
    <section class="hib-chapter" id="${text(meta.id)}" aria-labelledby="${text(meta.id)}-title" data-chapter="${text(meta.id)}">
      <div class="hib-chapter-heading">
        <p class="hib-kicker">${text(meta.kicker)}</p>
        <h2 id="${text(meta.id)}-title">${text(meta.title)}</h2>
        <p>${text(meta.summary)}</p>
      </div>
      ${body}
      ${sources(meta.sources)}
    </section>
  `;
}

function termCards(): string {
  return HOW_BUILT.product.terms
    .map(
      (item) => `
        <article class="hib-term">
          <h3>${text(item.name)}</h3>
          <p>${text(item.detail)}</p>
        </article>
      `,
    )
    .join("");
}

function stackCards(): string {
  return HOW_BUILT.stack
    .map(
      (item) => `
        <article class="hib-stack-card">
          <h3>${text(item.name)}</h3>
          <p>${text(item.detail)}</p>
          ${sourceLink(item.source)}
        </article>
      `,
    )
    .join("");
}

function modelCard(item: HowBuiltModelItem): string {
  return `
    <details class="hib-model-card">
      <summary>
        <span>
          <strong>${text(item.name)}</strong>
          <small>${text(item.codeName)}</small>
        </span>
        <span class="hib-disclosure" aria-hidden="true">+</span>
      </summary>
      <div class="hib-model-body">
        <p class="hib-model-meaning">${text(item.meaning)}</p>
        <dl>
          <div><dt>Important fields</dt><dd>${text(item.importantFields)}</dd></div>
          <div><dt>Owner or editor</dt><dd>${text(item.ownedBy)}</dd></div>
          <div><dt>What can change</dt><dd>${text(item.changes)}</dd></div>
          <div><dt>Current limit</dt><dd>${text(item.limit)}</dd></div>
        </dl>
        ${sourceLink(item.source)}
      </div>
    </details>
  `;
}

function renderProduct(): string {
  return chapter(
    howBuiltSection("product"),
    `
      <p class="hib-pullquote">${text(HOW_BUILT.product.origin)}</p>
      <div class="hib-term-grid">${termCards()}</div>
      <aside class="hib-plain-note">
        <strong>What the recipient gets</strong>
        <p>The owner card stays in the owner's workspace. Publishing writes a separate Library record with the chosen shared title, source, Shared Notes, format, and lane. Recipient responses omit the owner's private Notes, Summary, keywords, and Conversation assignments.</p>
      </aside>
    `,
  );
}

function renderStack(): string {
  return chapter(
    howBuiltSection("stack"),
    `
      <div class="hib-stack-grid">${stackCards()}</div>
      <p class="hib-caption">The public site, signed-in Web client, API gateway, backend, iPhone app, Android app, migrations, import tools, and tests live in the same private GitHub repository.</p>
      <div class="hib-subsection-heading">
        <p class="hib-kicker">Platform rollout</p>
        <h3>Source, installed, and accepted are different states.</h3>
      </div>
      <div class="hib-platform-grid">
        ${HOW_BUILT.platforms.map((platform) => `
          <article>
            <h3>${text(platform.name)}</h3>
            <p>${text(platform.state)}</p>
            <p class="hib-limit"><strong>Current limit:</strong> ${text(platform.limit)}</p>
            ${sourceLink(platform.source)}
          </article>
        `).join("")}
      </div>
    `,
  );
}

function movingPartStatus(status: string): string {
  return `<span class="hib-moving-status" data-status="${text(status)}">${text(status)}</span>`;
}

function renderMovingScreen(screen: MovingPartScreen): string {
  return `
    <article class="hib-screen-card" id="screen-${text(screen.id)}" data-screen-id="${text(screen.id)}" tabindex="-1">
      <div class="hib-screen-meta">
        <div>
          <p class="hib-kicker">${text(screen.platform)}</p>
          <h4>${text(screen.title)}</h4>
        </div>
        ${movingPartStatus(screen.status)}
      </div>
      <p class="hib-screen-purpose">${text(screen.purpose)}</p>
      <div class="hib-phone-frame" aria-label="Mockup of ${text(screen.title)}">
        <div class="hib-phone-status" aria-hidden="true"><span>9:41</span><span>● ● ●</span></div>
        <p class="hib-phone-eyebrow">${text(screen.eyebrow)}</p>
        <strong class="hib-phone-title">${text(screen.headline)}</strong>
        <ul>${screen.lines.map((line) => `<li>${text(line)}</li>`).join("")}</ul>
        <div class="hib-phone-actions">
          ${screen.actions.map((action) => `
            <button type="button" data-screen-target="${text(action.target)}" data-screen-origin="${text(screen.id)}" aria-label="${text(action.label)}. Show ${text(action.target.replaceAll("-", " "))}">
              ${text(action.label)} <span aria-hidden="true">→</span>
            </button>
          `).join("")}
        </div>
      </div>
      <dl class="hib-action-map">
        ${screen.actions.map((action) => `
          <div><dt>${text(action.label)} → ${text(action.target.replaceAll("-", " "))}</dt><dd>${text(action.effect)}</dd></div>
        `).join("")}
      </dl>
      ${sourceLink(screen.source)}
    </article>
  `;
}

function renderMovingProcess(process: MovingPartProcess): string {
  return `
    <details class="hib-process" id="process-${text(process.id)}">
      <summary>
        <span><strong>${text(process.title)}</strong><small>${text(process.trigger)}</small></span>
        ${movingPartStatus(process.status)}
      </summary>
      <div class="hib-process-body">
        <div class="hib-process-lane">
          <p>Tap or action</p>
          <strong>${text(process.trigger)}</strong>
        </div>
        <span class="hib-process-arrow" aria-hidden="true">→</span>
        <div class="hib-process-lane">
          <p>Immediate and local</p>
          <ol>${process.immediate.map((item) => `<li>${text(item)}</li>`).join("")}</ol>
        </div>
        <span class="hib-process-arrow" aria-hidden="true">→</span>
        <div class="hib-process-lane">
          <p>Async and canonical</p>
          <ol>${process.asynchronous.map((item) => `<li>${text(item)}</li>`).join("")}</ol>
        </div>
      </div>
      <p class="hib-process-failure"><strong>If interrupted or rejected:</strong> ${text(process.failure)}</p>
      <div class="hib-process-sources">${process.sources.map(sourceLink).join("")}</div>
    </details>
  `;
}

function renderMovingParts(): string {
  return chapter(
    MOVING_PARTS.section,
    `
      <p class="hib-pullquote">${text(MOVING_PARTS.intro.title)}</p>
      <p class="hib-moving-summary">${text(MOVING_PARTS.intro.summary)}</p>
      <aside class="hib-plain-note">
        <strong>Scope boundary</strong>
        <p>${text(MOVING_PARTS.intro.boundary)}</p>
      </aside>

      <div class="hib-subsection-heading">
        <p class="hib-kicker">What three weeks taught us</p>
        <h3>The decisions worth carrying into a clean implementation.</h3>
      </div>
      <div class="hib-learning-grid">
        ${MOVING_PARTS.learned.map((lesson, index) => `
          <article><span>${String(index + 1).padStart(2, "0")}</span><h4>${text(lesson.title)}</h4><p>${text(lesson.detail)}</p></article>
        `).join("")}
      </div>

      <div class="hib-subsection-heading">
        <p class="hib-kicker">Roadmap boundary</p>
        <h3>Enough status to orient the rebuild—without making a second Roadmap.</h3>
      </div>
      <div class="hib-rebuild-roadmap">
        ${MOVING_PARTS.roadmap.map((stage, index) => `
          <article data-roadmap-stage="${index + 1}">
            <span>${index + 1}</span>
            <div><h4>${text(stage.name)}</h4><p>${text(stage.detail)}</p><a href="${text(stage.href)}">Open the canonical source <span aria-hidden="true">→</span></a></div>
          </article>
        `).join("")}
      </div>

      <div class="hib-subsection-heading">
        <p class="hib-kicker">Screen atlas</p>
        <h3>Every canonical customer screen, with each action wired to its next state.</h3>
        <p>Tap a button inside a mockup to jump to the screen it opens. Built means current source exists; Release gate means the complete end-user or release proof is still open; Next is intentionally later.</p>
      </div>
      <p class="hib-screen-jump-status" data-screen-jump-status aria-live="polite"></p>
      <div class="hib-screen-groups">
        ${MOVING_PARTS.screenGroups.map((group, index) => `
          <details class="hib-screen-group"${index === 0 ? " open" : ""}>
            <summary><span><strong>${text(group.title)}</strong><small>${text(group.description)}</small></span><span>${MOVING_PARTS.screens.filter((screen) => screen.group === group.id).length} screens</span></summary>
            <div class="hib-screen-grid">
              ${MOVING_PARTS.screens.filter((screen) => screen.group === group.id).map(renderMovingScreen).join("")}
            </div>
          </details>
        `).join("")}
      </div>
      <aside class="hib-state-matrix">
        <p class="hib-kicker">Required state variants</p>
        <h3>The happy path is not a complete screen.</h3>
        <ul>${MOVING_PARTS.transientStates.map((state) => `<li>${text(state)}</li>`).join("")}</ul>
      </aside>

      <div class="hib-subsection-heading">
        <p class="hib-kicker">Action → side effects</p>
        <h3>What runs immediately, what continues in the background, and how it fails.</h3>
      </div>
      <div class="hib-process-list">
        ${MOVING_PARTS.processes.map(renderMovingProcess).join("")}
      </div>

      <div class="hib-subsection-heading">
        <p class="hib-kicker">One-shot build brief</p>
        <h3>A copyable prompt for rebuilding Convoful without losing the hard-won boundaries.</h3>
      </div>
      <details class="hib-one-shot" open>
        <summary>Open the complete implementation brief</summary>
        <div class="hib-one-shot-toolbar">
          <p>Use this as the durable starting prompt. Let the live Roadmap decide current sequencing.</p>
          <button type="button" data-copy-one-shot>Copy build brief</button>
        </div>
        <p class="hib-copy-status" data-copy-status aria-live="polite"></p>
        <pre tabindex="0" data-one-shot-text>${text(MOVING_PARTS.oneShotPrompt)}</pre>
      </details>
    `,
  );
}

function renderDataFlow(): string {
  return chapter(
    howBuiltSection("data-flow"),
    `
      <figure class="hib-flow-figure">
        <div class="hib-flow-visual" aria-hidden="true">
          <span>Write locally</span><b>→</b><span>Authorize the request</span><b>→</b><span>Append the change</span><b>→</b><span>Return an allowed view</span>
        </div>
        <figcaption>
          <ol>
            ${HOW_BUILT.dataFlow.steps.map((step) => `<li><strong>${text(step.name)}</strong><span>${text(step.detail)}</span></li>`).join("")}
          </ol>
        </figcaption>
      </figure>
      <div class="hib-location-grid">
        <article>
          <p class="hib-kicker">On a device</p>
          <h3>Working copies and unfinished drafts</h3>
          <ul>${HOW_BUILT.dataFlow.local.map((item) => `<li>${text(item)}</li>`).join("")}</ul>
        </article>
        <article>
          <p class="hib-kicker">In Cloudflare</p>
          <h3>The synced system of record</h3>
          <ul>${HOW_BUILT.dataFlow.cloud.map((item) => `<li>${text(item)}</li>`).join("")}</ul>
        </article>
      </div>
      <aside class="hib-plain-note">
        <strong>The encryption boundary</strong>
        <p>${text(HOW_BUILT.dataFlow.encryption)}</p>
      </aside>
      <div class="hib-subsection-heading">
        <p class="hib-kicker">Retention and deletion</p>
        <h3>What exists today, and what does not.</h3>
      </div>
      <ul class="hib-retention-list">
        ${HOW_BUILT.dataFlow.retention.map((item) => `<li>${text(item)}</li>`).join("")}
      </ul>
    `,
  );
}

function renderModel(): string {
  return chapter(
    howBuiltSection("model"),
    `
      <div class="hib-model-grid">
        ${HOW_BUILT.model.map(modelCard).join("")}
      </div>
      <p class="hib-caption">Open any object for its important fields, editor, mutability rule, current limit, and canonical source file.</p>
    `,
  );
}

function renderMedia(): string {
  return chapter(
    howBuiltSection("media"),
    `
      <div class="hib-media-grid">
        <article>
          <p class="hib-kicker">Saved-item media</p>
          <h3>The schema can hold four; today's creation UI cannot.</h3>
          <p>${text(HOW_BUILT.media.savedItem)}</p>
          <p class="hib-callout"><strong>Mark's three-screenshot case:</strong> ${text(HOW_BUILT.media.screenshots)}</p>
        </article>
        <figure class="hib-card-back-figure">
          <div class="hib-card-fan" aria-hidden="true">
            <span class="hib-photo hib-photo-one"></span>
            <span class="hib-photo hib-photo-two"></span>
            <span class="hib-photo hib-photo-three"></span>
          </div>
          <figcaption>
            <strong>Three cards, three decorative phone photos.</strong>
            <span>${text(HOW_BUILT.media.cardBack)}</span>
          </figcaption>
        </figure>
      </div>
    `,
  );
}

function renderSearch(): string {
  return chapter(
    howBuiltSection("search"),
    `
      <div class="hib-search-grid">
        <div>
          <p>${text(HOW_BUILT.search.designed)}</p>
          <pre aria-label="Search weighting pseudocode"><code>${HOW_BUILT.search.formula.map(text).join("\n")}</code></pre>
        </div>
        <aside class="hib-plain-note">
          <strong>Current production truth</strong>
          <p>${text(HOW_BUILT.search.current)}</p>
        </aside>
      </div>
      <div class="hib-subsection-heading">
        <p class="hib-kicker">Measured search evidence</p>
        <h3>Why 50/25/25 stayed in the contract.</h3>
      </div>
      <div class="hib-evaluation-grid">
        <article>
          <h3>Starter benchmark</h3>
          <p>${text(HOW_BUILT.search.evaluation.benchmark)}</p>
          <p>${text(HOW_BUILT.search.evaluation.nearbyProfiles)}</p>
        </article>
        <article>
          <h3>Post-hoc challenger</h3>
          <p>${text(HOW_BUILT.search.evaluation.sensitivity)}</p>
          <p>${text(HOW_BUILT.search.evaluation.holdout)}</p>
        </article>
        <article>
          <h3>Relevance pilot</h3>
          <p>${text(HOW_BUILT.search.evaluation.relevancePilot)}</p>
        </article>
        <article>
          <h3>Enrichment rehearsal</h3>
          <p>${text(HOW_BUILT.search.evaluation.enrichment)}</p>
        </article>
      </div>
    `,
  );
}

function renderImports(): string {
  return chapter(
    howBuiltSection("imports-sync"),
    `
      <div class="hib-import-layout">
        <div>
          <p class="hib-kicker">Current source mix</p>
          <dl class="hib-source-counts">
            ${HOW_BUILT.imports.sources.map((item) => `<div><dt>${text(item.name)}</dt><dd><strong>${text(item.count)}</strong><span>${text(item.detail)}</span></dd></div>`).join("")}
          </dl>
          <p class="hib-caption">Read-only production aggregates checked July 30, 2026. They will change.</p>
        </div>
        <article class="hib-sync-explainer">
          <p class="hib-kicker">Sync without jargon</p>
          <h3>The phone remembers the last accepted change</h3>
          <p>${text(HOW_BUILT.imports.sync)}</p>
        </article>
      </div>
    `,
  );
}

function renderWorkflow(): string {
  return chapter(
    howBuiltSection("workflow"),
    `
      <ol class="hib-workflow">
        ${HOW_BUILT.workflow.stages.map((stage) => `<li><strong>${text(stage.name)}</strong><span>${text(stage.detail)}</span></li>`).join("")}
      </ol>
      <div class="hib-subsection-heading">
        <p class="hib-kicker">Agent ownership</p>
        <h3>Who changes what.</h3>
      </div>
      <div class="hib-stack-grid">
        ${HOW_BUILT.workflow.roles.map((role) => `
          <article class="hib-stack-card">
            <h3>${text(role.name)}</h3>
            <p>${text(role.detail)}</p>
            ${sourceLink(role.source)}
          </article>
        `).join("")}
      </div>
      <aside class="hib-plain-note">
        <strong>About release checkpoints</strong>
        <p>${text(HOW_BUILT.workflow.releaseCheckpoints)}</p>
      </aside>
    `,
  );
}

function renderLessons(): string {
  return chapter(
    howBuiltSection("lessons"),
    `
      <div class="hib-lessons-grid">
        <article>
          <p class="hib-kicker">What worked</p>
          <ul>${HOW_BUILT.lessons.worked.map((item) => `<li>${text(item)}</li>`).join("")}</ul>
        </article>
        <article>
          <p class="hib-kicker">What failed</p>
          <ul>${HOW_BUILT.lessons.failed.map((item) => `<li>${text(item)}</li>`).join("")}</ul>
        </article>
      </div>
      <aside class="hib-plain-note">
        <strong>When Mark does not answer</strong>
        <p>${text(HOW_BUILT.lessons.withoutMark)}</p>
      </aside>
    `,
  );
}

function renderNow(): string {
  return chapter(
    howBuiltSection("now"),
    `
      <div class="hib-now-card">
        <p>${text(HOW_BUILT.current.copy)}</p>
        <div class="hib-actions">
          ${HOW_BUILT.current.links.map((link, index) => `<a class="${index === 0 ? "hib-primary-action" : "hib-secondary-action"}" href="${text(link.href)}">${text(link.label)}<span aria-hidden="true"> →</span></a>`).join("")}
        </div>
      </div>
    `,
  );
}

function chapterNavItems(): string {
  const links: string[] = [];
  HOW_BUILT.sections.forEach((item, index) => {
    const displayIndex = index < 2 ? index + 1 : index + 2;
    links.push(`<a href="#${text(item.id)}" data-nav-for="${text(item.id)}"><span>${String(displayIndex).padStart(2, "0")}</span>${text(item.nav)}</a>`);
    if (item.id === "stack") {
      links.push(`<a href="#${text(MOVING_PARTS.section.id)}" data-moving-nav-for="${text(MOVING_PARTS.section.id)}"><span>03</span>${text(MOVING_PARTS.section.nav)}</a>`);
    }
  });
  return links.join("");
}

export function renderHowBuilt(root: HTMLElement): void {
  activePage?.abort();
  activePage = new AbortController();
  const { signal } = activePage;

  document.title = `How I built ${PRODUCT.name} · From saved links to conversation cards`;
  document.documentElement.dataset.surface = "how-built";
  const skipLink = document.querySelector<HTMLAnchorElement>(".skip-link");
  if (skipLink) skipLink.href = "#how-built-main";
  setMetaDescription(
    "How Mark built Convoful: the Maya problem, Swift and Kotlin apps, Cloudflare data model, sync, Search ranking, imports, agent workflow, failures, and current beta state.",
  );
  setCanonical(`${WEB_ROUTES.canonicalOrigin}${WEB_ROUTES.howBuilt}`);

  root.innerHTML = `
    <div class="hib-shell" data-mode="guide">
      <header class="hib-header">
        <a class="hib-brand" href="${WEB_ROUTES.marketing}" aria-label="${text(PRODUCT.name)} home">
          <span class="brand-mark brand-mark-small" aria-hidden="true">${PRODUCT.brandMark}</span>
          <span>${text(PRODUCT.name)}</span>
        </a>
        <div class="hib-mode-switch" role="group" aria-label="Reading mode">
          <button type="button" aria-pressed="true" data-mode-button="guide">Guide</button>
          <button type="button" aria-pressed="false" data-mode-button="presentation">Presentation</button>
        </div>
        <a class="hib-open-app" href="${WEB_ROUTES.app}">Open private beta</a>
      </header>

      <main id="how-built-main" tabindex="-1">
        <section class="hib-hero" id="overview" aria-labelledby="hib-title" data-chapter="overview">
          <div class="hib-hero-copy">
            <p class="hib-kicker">${text(HOW_BUILT.hero.kicker)}</p>
            <h1 id="hib-title">${text(HOW_BUILT.hero.title)}</h1>
            <p class="hib-lede">${text(HOW_BUILT.hero.lede)}</p>
            <a class="hib-primary-action" href="#product">Start with the product <span aria-hidden="true">↓</span></a>
          </div>
          <aside class="hib-short-version" aria-label="The short version">
            <p class="hib-kicker">The short version</p>
            <ul>${HOW_BUILT.hero.shortVersion.map((item) => `<li>${text(item)}</li>`).join("")}</ul>
          </aside>
          <dl class="hib-snapshot" aria-label="Historical aggregate measurement">
            <div><dt>Measured</dt><dd>${text(HOW_BUILT.historicalMeasurement.measuredAt)}</dd></div>
            <div><dt>Saved items</dt><dd>${text(HOW_BUILT.historicalMeasurement.canonicalCards.toLocaleString())}</dd></div>
            <div><dt>Media records</dt><dd>${text(HOW_BUILT.historicalMeasurement.mediaReferences)}</dd></div>
            <div><dt>Cards with media</dt><dd>${text(HOW_BUILT.historicalMeasurement.cardsWithMedia)}</dd></div>
            <div><dt>Library publications</dt><dd>${text(HOW_BUILT.historicalMeasurement.liveLibraryPublications)}</dd></div>
          </dl>
          <p class="hib-caption">${text(HOW_BUILT.historicalMeasurement.copy)}</p>
        </section>

        <details class="hib-mobile-nav">
          <summary>Chapters</summary>
          <nav aria-label="How I Built This chapters">${chapterNavItems()}</nav>
        </details>

        <div class="hib-reading-layout">
          <aside class="hib-rail">
            <p class="hib-kicker">Chapters</p>
            <nav aria-label="How I Built This chapters">
              ${chapterNavItems()}
            </nav>
          </aside>
          <div class="hib-story">
            ${renderProduct()}
            ${renderStack()}
            ${renderMovingParts()}
            ${renderDataFlow()}
            ${renderModel()}
            ${renderMedia()}
            ${renderSearch()}
            ${renderImports()}
            ${renderWorkflow()}
            ${renderLessons()}
            ${renderNow()}
          </div>
        </div>
      </main>

      <div class="hib-presentation-controls" aria-label="Presentation controls">
        <button type="button" data-slide-action="previous">← Previous</button>
        <span aria-live="polite" data-slide-status>1 of ${HOW_BUILT.sections.length + 2}</span>
        <button type="button" data-slide-action="next">Next →</button>
      </div>

      <footer class="hib-footer">
        <span><strong>${text(PRODUCT.name)}</strong> · a private beta built with Mark and Codex</span>
        <a href="${WEB_ROUTES.marketing}">Back to Convoful <span aria-hidden="true">→</span></a>
      </footer>
    </div>
  `;

  const shell = root.querySelector<HTMLElement>(".hib-shell");
  if (!shell) return;
  const chapters = Array.from(root.querySelectorAll<HTMLElement>("[data-chapter]"));
  const modeButtons = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-mode-button]"));
  const slideStatus = root.querySelector<HTMLElement>("[data-slide-status]");
  const initialHash = window.location.hash.slice(1);
  let currentChapter = Math.max(
    0,
    chapters.findIndex((item) => item.id === initialHash),
  );

  const setCurrent = (index: number, scroll = false): void => {
    currentChapter = Math.max(0, Math.min(index, chapters.length - 1));
    const current = chapters[currentChapter];
    root.querySelectorAll<HTMLAnchorElement>("[data-nav-for], [data-moving-nav-for]").forEach((link) => {
      if ((link.dataset.navFor ?? link.dataset.movingNavFor) === current.id) link.setAttribute("aria-current", "location");
      else link.removeAttribute("aria-current");
    });
    if (slideStatus) slideStatus.textContent = `${currentChapter + 1} of ${chapters.length}`;
    if (scroll) {
      current.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      history.replaceState(null, "", `#${current.id}`);
    }
  };

  const setMode = (mode: "guide" | "presentation"): void => {
    shell.dataset.mode = mode;
    modeButtons.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.modeButton === mode)));
    if (mode === "presentation") setCurrent(currentChapter, true);
  };

  modeButtons.forEach((button) => {
    button.addEventListener("click", () => setMode(button.dataset.modeButton === "presentation" ? "presentation" : "guide"), { signal });
  });

  root.querySelector<HTMLButtonElement>('[data-slide-action="previous"]')?.addEventListener("click", () => setCurrent(currentChapter - 1, true), { signal });
  root.querySelector<HTMLButtonElement>('[data-slide-action="next"]')?.addEventListener("click", () => setCurrent(currentChapter + 1, true), { signal });

  root.querySelectorAll<HTMLButtonElement>("[data-screen-target]").forEach((button) => {
    button.addEventListener("click", () => {
      const targetID = button.dataset.screenTarget;
      if (!targetID) return;
      const target = root.querySelector<HTMLElement>(`[data-screen-id="${targetID}"]`);
      if (!target) return;
      const group = target.closest<HTMLDetailsElement>(".hib-screen-group");
      if (group) group.open = true;
      root.querySelectorAll<HTMLElement>("[data-screen-id]").forEach((item) => item.removeAttribute("data-screen-active"));
      target.dataset.screenActive = "true";
      target.focus({ preventScroll: true });
      target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
      const status = root.querySelector<HTMLElement>("[data-screen-jump-status]");
      if (status) status.textContent = `${button.textContent?.replace("→", "").trim() ?? "Action"} opens ${target.querySelector("h4")?.textContent ?? targetID}.`;
    }, { signal });
  });

  root.querySelector<HTMLButtonElement>("[data-copy-one-shot]")?.addEventListener("click", async () => {
    const status = root.querySelector<HTMLElement>("[data-copy-status]");
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(MOVING_PARTS.oneShotPrompt);
      if (status) status.textContent = "Build brief copied.";
    } catch {
      if (status) status.textContent = "Clipboard access is unavailable here. Select the complete brief below to copy it.";
    }
  }, { signal });

  document.addEventListener(
    "keydown",
    (event) => {
      if (shell.dataset.mode !== "presentation") return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, button, a, summary, details")) return;
      if (event.key === "Escape") {
        setMode("guide");
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        setCurrent(0, true);
      } else if (event.key === "End") {
        event.preventDefault();
        setCurrent(chapters.length - 1, true);
      } else if (["PageDown", "ArrowRight", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        setCurrent(currentChapter + 1, true);
      } else if (["PageUp", "ArrowLeft", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        setCurrent(currentChapter - 1, true);
      }
    },
    { signal },
  );

  const openedForPrint = new Set<HTMLDetailsElement>();
  window.addEventListener(
    "beforeprint",
    () => {
      openedForPrint.clear();
      root.querySelectorAll<HTMLDetailsElement>(".hib-model-card, .hib-sources, .hib-screen-group, .hib-process, .hib-one-shot").forEach((details) => {
        if (details.open) return;
        details.open = true;
        openedForPrint.add(details);
      });
    },
    { signal },
  );
  window.addEventListener(
    "afterprint",
    () => {
      openedForPrint.forEach((details) => {
        details.open = false;
      });
      openedForPrint.clear();
    },
    { signal },
  );

  const updateCurrentFromScroll = (): void => {
    const readingLine = window.innerHeight * 0.32;
    let nearest = 0;
    chapters.forEach((item, index) => {
      if (item.getBoundingClientRect().top <= readingLine) nearest = index;
    });
    setCurrent(nearest);
  };
  window.addEventListener("scroll", updateCurrentFromScroll, {
    passive: true,
    signal,
  });
  window.addEventListener("resize", updateCurrentFromScroll, { signal });
  setCurrent(currentChapter);
  if (initialHash) {
    chapters[currentChapter].scrollIntoView({ behavior: "auto" });
    requestAnimationFrame(updateCurrentFromScroll);
  }
}

function setMetaDescription(content: string): void {
  let description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!description) {
    description = document.createElement("meta");
    description.name = "description";
    document.head.append(description);
  }
  description.content = content;
}

function setCanonical(href: string): void {
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = href;
}
